import React from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { ErrorOutline, Refresh, Home, Warning } from '@mui/icons-material';

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  // Check if it's a chunk loading error
  const isChunkError = (error: any) => {
    return (
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.name === 'ChunkLoadError' ||
      error?.toString?.()?.includes('ChunkLoadError')
    );
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRetry = () => {
    // For chunk errors, we need to reload the page to get fresh chunks
    if (isChunkError(error)) {
      window.location.reload();
    } else {
      // For other errors, try navigating back
      window.history.back();
    }
  };

  // Handle different types of errors
  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';
  let showTechnicalDetails = false;

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page Not Found';
      message = 'The page you are looking for could not be found.';
    } else if (error.status === 500) {
      title = 'Server Error';
      message = 'A server error occurred. Please try again later.';
    }
  } else if (isChunkError(error)) {
    title = 'Loading Error';
    message = 'Failed to load the page content. This usually happens after an app update. Refreshing the page should fix this.';
    showTechnicalDetails = true;
  } else if (error instanceof Error) {
    if (error.message.includes('NetworkError')) {
      title = 'Network Error';
      message = 'Please check your internet connection and try again.';
    }
    showTechnicalDetails = true;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
        {isChunkError(error) ? (
          <Warning color="warning" sx={{ fontSize: 48, mb: 2 }} />
        ) : (
          <ErrorOutline color="error" sx={{ fontSize: 48, mb: 2 }} />
        )}

        <Typography variant="h4" color={isChunkError(error) ? 'warning.main' : 'error'} gutterBottom>
          {title}
        </Typography>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {message}
        </Typography>

        {isChunkError(error) && (
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>What happened?</strong> The app has been updated with new features and improvements.
              Your browser is trying to load an outdated version of the page.
              <br /><br />
              <strong>Quick fix:</strong> Click "Refresh Page" below to load the latest version.
            </Typography>
          </Alert>
        )}

        {showTechnicalDetails && error && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 3,
              fontFamily: 'monospace',
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              maxHeight: 200,
              overflow: 'auto',
              textAlign: 'left'
            }}
          >
            {error instanceof Error ? error.message : String(error)}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          {isChunkError(error) ? (
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              color="warning"
            >
              Refresh Page
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRetry}
              >
                Try Again
              </Button>
              <Button
                variant="contained"
                startIcon={<Home />}
                onClick={handleGoHome}
              >
                Go Home
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}